import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconButton, Menu, MenuItem, SvgIcon } from '@mui/material';
import ButtonWithConfirmationDialog from 'components/buttons/ButtonWithConfirmationDialog';
import { useDeleteBlueprint } from 'features/blueprints';
import { Icons } from 'greener-ui-library';
import { useSnackbar } from 'notistack';

interface DeleteBlueprintButtonProps {
  blueprintId: number;
  blueprintName: string;
}

export const DeleteBlueprintButton = ({ blueprintId, blueprintName }: DeleteBlueprintButtonProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deleteBlueprint, isLoading } = useDeleteBlueprint();
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteBlueprint(blueprintId);
      navigate('/solutions');
      enqueueSnackbar(
        t('genericSuccesses.delete', { entity: t('entities:blueprint.nameSingular'), identifier: blueprintName }),
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar(
        t('genericErrors.delete', { entity: t('entities:blueprint.nameSingular'), identifier: blueprintName }),
        { variant: 'error' }
      );
    }
  };

  return (
    <>
      <IconButton onClick={handleMenuClick}>
        <Icons.More />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ButtonWithConfirmationDialog
          buttonText={
            <>
              <SvgIcon fontSize="small" color="inherit" sx={{ marginRight: '0.5em' }}>
                <Icons.Delete />
              </SvgIcon>
              {t('components:form.delete')}
            </>
          }
          title={t('blueprints:details.deleteDialog.title')}
          confirmButtonText={t('components:form.delete')}
          handleConfirm={handleConfirmDelete}
          ButtonComponent={MenuItem}
          onClose={handleMenuClose}
          dialogConfirmButtonProps={{ disabled: isLoading }}
        >
          {t('blueprints:details.deleteDialog.content', { name: blueprintName })}
        </ButtonWithConfirmationDialog>
      </Menu>
    </>
  );
};
